/**
 * Admin navigation section for the sidebar.
 *
 * Contains admin-specific nav items (dashboard, users, releases, debug)
 * and the collapsible section UI with pure CSS animations.
 * Uses data-sidebar-* attributes for CSS-based collapse state.
 */

import * as stylex from "@stylexjs/stylex";

import { hasMinimumRole, type Role } from "@animus/frontend-features/rbac";
import { apple } from "@animus/iconic-svg/icons/apple";
import { bug } from "@animus/iconic-svg/icons/bug";
import { chevronDown } from "@animus/iconic-svg/icons/chevron-down";
import { dashboard } from "@animus/iconic-svg/icons/dashboard";
import { rocket } from "@animus/iconic-svg/icons/rocket";
import { slidersHorizontal } from "@animus/iconic-svg/icons/sliders-horizontal";
import { users } from "@animus/iconic-svg/icons/users";
import {
  CollapsibleContent,
  Icon,
  SidebarLinkMotion,
  SidebarSectionHeaderMotion,
  sidebar,
} from "@animus/primaris";

import type { SidebarDestination } from "../../capabilities/navigation";
import { useNavigationSlots } from "../../capabilities/navigation";

interface AdminNavItemDef {
  destination: SidebarDestination;
  label: string;
  icon: string;
  requiredRole: Role;
}

const ADMIN_NAV_ITEMS: AdminNavItemDef[] = [
  {
    destination: "adminDashboard",
    label: "Dashboard",
    icon: dashboard,
    requiredRole: "moderator",
  },
  {
    destination: "adminUsers",
    label: "Users",
    icon: users,
    requiredRole: "moderator",
  },
  {
    destination: "adminFoods",
    label: "Foods",
    icon: apple,
    requiredRole: "moderator",
  },
  {
    destination: "adminModifiers",
    label: "Modifiers",
    icon: slidersHorizontal,
    requiredRole: "moderator",
  },
  {
    destination: "adminReleases",
    label: "Releases",
    icon: rocket,
    requiredRole: "moderator",
  },
  {
    destination: "adminDebug",
    label: "Debug",
    icon: bug,
    requiredRole: "moderator",
  },
];

function filterAdminItemsByRole(userRole: Role | null): AdminNavItemDef[] {
  if (!userRole) {
    return [];
  }
  return ADMIN_NAV_ITEMS.filter((item) =>
    hasMinimumRole(userRole, item.requiredRole)
  );
}

function AdminNavItem({
  item,
  isCollapsed,
}: {
  item: AdminNavItemDef;
  isCollapsed: boolean;
}) {
  const { NavLink } = useNavigationSlots();

  return (
    <li>
      <NavLink destination={item.destination}>
        {({ isActive, navigate }) => (
          <SidebarLinkMotion
            data-sidebar-link
            isCollapsed={isCollapsed}
            {...stylex.props(
              sidebar.link,
              isActive && sidebar.linkActive,
              isActive && sidebar.linkActiveIndicator
            )}
            onClick={navigate}
            type="button"
          >
            <Icon
              data-sidebar-icon
              size="sm"
              svg={item.icon}
              xstyle={[sidebar.navIcon, isActive && sidebar.navIconActive]}
            />
            <span data-sidebar-label {...stylex.props(sidebar.linkLabel)}>
              {item.label}
            </span>
          </SidebarLinkMotion>
        )}
      </NavLink>
    </li>
  );
}

export interface AdminNavSectionProps {
  userRole: Role | null;
  isExpanded: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
}

export function AdminNavSection({
  userRole,
  isExpanded,
  isCollapsed,
  onToggle,
}: AdminNavSectionProps) {
  const visibleItems = filterAdminItemsByRole(userRole);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div data-sidebar-section-group {...stylex.props(sidebar.sectionGroup)}>
      <SidebarSectionHeaderMotion
        data-sidebar-section-header-wrapper
        isCollapsed={isCollapsed}
        {...stylex.props(sidebar.sectionHeaderWrapper)}
      >
        <button
          {...stylex.props(sidebar.sectionHeader)}
          aria-expanded={isExpanded}
          onClick={onToggle}
          type="button"
        >
          <span {...stylex.props(sidebar.sectionLabel)}>Admin</span>
          <Icon
            data-sidebar-section-chevron
            size="xs"
            svg={chevronDown}
            xstyle={[
              sidebar.sectionChevron,
              !isExpanded && sidebar.sectionChevronCollapsed,
            ]}
          />
        </button>
      </SidebarSectionHeaderMotion>
      <CollapsibleContent isExpanded={isExpanded}>
        <ul data-sidebar-nav {...stylex.props(sidebar.sectionContentInner)}>
          {visibleItems.map((item) => (
            <AdminNavItem
              isCollapsed={isCollapsed}
              item={item}
              key={item.destination}
            />
          ))}
        </ul>
      </CollapsibleContent>
    </div>
  );
}
